// Shared timezone utilities and formatting helpers for Lightweight Charts
// Keep time formatting consistent across charts/reports

export type TimezoneMode = 'UTC' | 'Local';

export const TIMEZONE_STORAGE_KEY = 'chart-timezone';

export function loadTimezone(): TimezoneMode {
  try {
    const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY) as TimezoneMode | null;
    if (stored === 'UTC' || stored === 'Local') return stored;
  } catch {
    // ignore localStorage errors
  }
  return 'UTC';
}

export function saveTimezone(tz: TimezoneMode): void {
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, tz);
  } catch {
    // ignore
  }
}

// Format crosshair/time labels (e.g., "Nov 12, 14:05" + optional " UTC")
export function formatCrosshairLabel(timeSec: number, tz: TimezoneMode): string {
  const d = new Date(timeSec * 1000);
  const opts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  if (tz === 'UTC') {
    return d.toLocaleString('en-US', { ...opts, timeZone: 'UTC' }) + ' UTC';
  }
  return d.toLocaleString('en-US', opts);
}

// Format legend label – keep consistent with crosshair label
export function formatLegendLabel(timeSec: number, tz: TimezoneMode): string {
  return formatCrosshairLabel(timeSec, tz);
}

// Format tick marks on time scale. tickMarkType: 0=Year,1=Month,2=Day,3=Time,4=TimeWithSeconds
export function formatTickMarkLabel(timeSec: number, tickMarkType: number, tz: TimezoneMode): string {
  const d = new Date(timeSec * 1000);
  const toStr = (o: Intl.DateTimeFormatOptions) =>
    d.toLocaleString('en-US', tz === 'UTC' ? { ...o, timeZone: 'UTC' } : o);

  if (tickMarkType === 3 || tickMarkType === 4) {
    const hour = toStr({ hour: '2-digit', hour12: false });
    const minute = toStr({ minute: '2-digit' });
    return `${hour}:${minute}`;
  } else if (tickMarkType === 2) {
    return toStr({ day: 'numeric' });
  } else if (tickMarkType === 1) {
    return toStr({ month: 'short' });
  }
  return toStr({ year: 'numeric' });
}

// Format timestamp for modals/detail views (returns structured date/time object)
export function formatModalTimestamp(timestamp: string | Date, tz: TimezoneMode): { date: string; time: string } {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);

  if (tz === 'UTC') {
    return {
      date: d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
      }),
      time: d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC'
      }) + ' UTC',
    };
  } else {
    return {
      date: d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: d.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
    };
  }
}

